import {
  BRANCH_NONE,
  QUESTIONS,
  YES_NO_OPTIONS,
  type Question,
  type QuestionnaireAnswer,
  type QuestionnaireAnswers,
} from '@nemo/core/talent/questionnaire';

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' });

/** "2027-03" as "Mar 2027"; anything else as it was written. */
function formatMonth(value: string): string {
  const parts = /^(\d{4})-(\d{2})$/.exec(value);
  if (parts === null) return value;
  return MONTH.format(new Date(Number(parts[1]), Number(parts[2]) - 1, 1));
}

/** An option's label, or the stored code where the questionnaire no longer offers it. */
export function optionLabel(question: Question, value: string): string {
  switch (question.type) {
    case 'single':
    case 'multi':
      return question.options.find((option) => option.value === value)?.label ?? value;
    case 'yes_no':
      return YES_NO_OPTIONS.find((option) => option.value === value)?.label ?? value;
    case 'text':
    case 'month':
      return value;
    default:
      return unreachable(question);
  }
}

/** One answer as a person reads it. */
export function answerText(question: Question, answer: QuestionnaireAnswer): string {
  if (Array.isArray(answer)) return answer.map((value) => optionLabel(question, value)).join(', ');
  if (question.type === 'month') return formatMonth(answer);
  return optionLabel(question, answer);
}

/** Every answered question, in the questionnaire's order, as label and text. */
export function answeredQuestions(
  answers: QuestionnaireAnswers,
): { id: string; label: string; text: string }[] {
  return QUESTIONS.flatMap((question) => {
    const answer = answers[question.id];
    if (answer === undefined) return [];
    return [{ id: question.id, label: question.label, text: answerText(question, answer) }];
  });
}

function unreachable(value: never): never {
  throw new Error(`Unhandled question: ${JSON.stringify(value)}`);
}

const BY_ID = new Map(QUESTIONS.map((question) => [question.id, question]));

/** One answer as the recruiter view shows it: the question's short label and each value in words. */
export interface ShortAnswer {
  id: string;
  label: string;
  values: string[];
}

/** The answer to one question by id; null when it is unanswered or no longer asked. */
export function shortAnswer(answers: QuestionnaireAnswers, id: string): ShortAnswer | null {
  const question = BY_ID.get(id);
  const answer = answers[id];
  if (question === undefined || answer === undefined) return null;
  const values = Array.isArray(answer)
    ? answer.map((value) => optionLabel(question, value))
    : [answerText(question, answer)];
  return { id, label: question.shortLabel, values };
}

/** The candidate said they never served, which the view shows instead of a service record. */
export function saidNoService(answers: QuestionnaireAnswers): boolean {
  return answers.branch === BRANCH_NONE;
}
